import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { tournamentAPI } from '../../api/client';
import { TOURNAMENT_ID } from '../../config';
import {
    ROUNDS, ROUND1_MATCHES, UPPER, LOWER, DROPS, OUTS,
    FINAL_CODE, UPPER_FINAL_CODE, matchCode, matchLabel,
} from './bracketStructure';
import {
    indexTournament, playerAt, winnerOf, formatPoints, formatDate, plural,
} from './tournamentData';
import './ChessTournament.css';

const TROPHY_IMAGE_URL = 'https://i.imgur.com/Upn2Ftu.jpg';

function describeError(err) {
    if (err?.response) return `Сервер ответил ошибкой ${err.response.status}.`;
    return 'Не удалось связаться с сервером.';
}

/* ---------- Карточки ---------- */

function PlayerRow({ player }) {
    const state = player?.result === 'WIN' ? 'win' : player?.result === 'LOSE' ? 'lose' : undefined;
    return (
        <div className={`ct-slot${state === 'win' ? ' is-win' : ''}`}>
            <span className={`ct-name${player ? '' : ' is-empty'}`} title={player?.name}>
                {player ? player.name : '—'}
            </span>
            <span className="ct-sc">{formatPoints(player)}</span>
            <span className="ct-strip" data-state={state} />
        </div>
    );
}

function MatchCard({ t, bracket, round, index, kind }) {
    const match = t.getMatch(matchCode(bracket, round, index));
    return (
        <div className={`ct-match ${kind}`}>
            <div className="ct-mid">
                <span>{matchLabel(bracket, round, index)}</span>
                <span>{formatDate(match?.date)}</span>
            </div>
            <PlayerRow player={playerAt(match, 1)} />
            <PlayerRow player={playerAt(match, 2)} />
        </div>
    );
}

function ByeCard({ t, bracket, round, kind }) {
    const match = t.getMatch(matchCode(bracket, round));
    const player = match?.players[0] ?? null;
    return (
        <div className={`ct-match ct-bye ${kind}`}>
            <div className="ct-mid">
                <span>{matchLabel(bracket, round)} · БАЙ</span>
                <span>без игры</span>
            </div>
            <div className="ct-slot ct-slot--plain">
                <span className={`ct-name${player ? '' : ' is-empty'}`} title={player?.name}>
                    {player ? player.name : '—'}
                </span>
            </div>
        </div>
    );
}

const at = (column, row) => ({ gridColumn: column, gridRow: row });

// Высота блока с сеткой подгоняется под окно так, чтобы на первом экране были видны
// и горизонтальная полоса прокрутки, и пояснения под сеткой — без прокрутки страницы.
const MIN_SCROLLER_HEIGHT = 320;
const PAGE_BOTTOM_GAP = 12;

function useFitToWindow(ref, belowRef, deps) {
    useLayoutEffect(() => {
        const fit = () => {
            const el = ref.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top + window.scrollY;
            const below = belowRef.current;
            const belowHeight = below
                ? below.offsetHeight + parseFloat(window.getComputedStyle(below).marginTop || 0)
                : 0;
            const height = Math.max(
                MIN_SCROLLER_HEIGHT,
                window.innerHeight - top - belowHeight - PAGE_BOTTOM_GAP,
            );
            el.style.maxHeight = `${height}px`;
        };
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

/* ---------- Страница ---------- */

export default function ChessTournament() {
    const [state, setState] = useState({ status: 'loading', data: null, error: null });

    const load = useCallback(() => {
        setState((s) => ({ ...s, status: 'loading', error: null }));
        tournamentAPI.getData(TOURNAMENT_ID)
            .then((res) => setState({ status: 'ok', data: res.data, error: null }))
            .catch((err) => setState((s) => ({ status: 'error', data: s.data, error: describeError(err) })));
    }, []);

    useEffect(() => { load(); }, [load]);

    const t = useMemo(() => indexTournament(state.data), [state.data]);
    const scrollerRef = useRef(null);
    const howRef = useRef(null);
    useFitToWindow(scrollerRef, howRef, [state.status]);
    const upperFinalist = winnerOf(t.getMatch(UPPER_FINAL_CODE));
    const finalMatch = t.getMatch(FINAL_CODE);
    const champion = winnerOf(finalMatch);
    const count = t.playersCount || 22;

    return (
        <div className="ct-page">
            <header className="ct-top">
                <div>
                    <p className="ct-eyebrow">Шахматы · двойное выбывание</p>
                    <h1 className="ct-title">{t.title || 'Шахматный турнир'}</h1>
                </div>
                <div className="ct-facts">
                    <span><b>{count}</b>{plural(count, ['участник', 'участника', 'участников'])}</span>
                    <span><b>8</b>туров</span>
                    <span><b>2</b>поражения — выбывание</span>
                </div>
            </header>

            {state.status === 'loading' && (
                <p className="ct-status" role="status">Загружаем данные турнира…</p>
            )}
            {state.status === 'error' && (
                <p className="ct-status ct-status--error" role="alert">
                    {state.error} {state.data ? 'Показаны последние полученные данные.' : 'Сетка пока пустая.'}
                    <button type="button" className="ct-retry" onClick={load}>Повторить</button>
                </p>
            )}

            <div className="ct-legend">
                <span><i className="ct-sw ct-sw--u" />Верхняя сетка — ещё без поражений</span>
                <span><i className="ct-sw ct-sw--l" />Нижняя сетка — одно поражение</span>
                <span><i className="ct-sw ct-sw--b" />Бай — проходит дальше без игры</span>
                <span><i className="ct-sw ct-sw--win" />Прошёл дальше</span>
                <span><i className="ct-sw ct-sw--lose" />Проиграл матч</span>
            </div>

            <div className="ct-scroller" ref={scrollerRef}>
                <div className="ct-grid">
                    <div className="ct-band ct-band--u" />
                    <div className="ct-band ct-band--l" />

                    {ROUNDS.map(([name, stage], i) => (
                        <div key={name} className="ct-th" style={at(i + 1)}>
                            <i className="ct-sq" />
                            <h3>{name}</h3>
                            <span className="ct-st">{stage}</span>
                        </div>
                    ))}

                    {/* Тур 1 */}
                    <div className="ct-cell ct-cell--t1">
                        {Array.from({ length: ROUND1_MATCHES }, (_, i) => (
                            <MatchCard key={i} t={t} bracket="T" round={1} index={i + 1} kind="ct-t1m" />
                        ))}
                        <div className="ct-t1foot">
                            <div className="ct-chip ct-chip--u"><span className="ct-ar">↗</span><span>{ROUND1_MATCHES} победителей — в верхнюю сетку</span></div>
                            <div className="ct-chip ct-chip--l"><span className="ct-ar">↘</span><span>{ROUND1_MATCHES} проигравших — в нижнюю сетку</span></div>
                        </div>
                    </div>

                    {/* Верхняя сетка */}
                    {Object.entries(UPPER).map(([r, u]) => {
                        const round = Number(r);
                        return [
                            <div key={`ul${r}`} className="ct-lane ct-lane--u" style={at(round)}>
                                <span>Верхняя</span><span className="ct-cnt">{u.count}</span>
                            </div>,
                            <div key={`uc${r}`} className="ct-cell ct-cell--u" style={at(round)}>
                                {u.bye && <ByeCard t={t} bracket="B" round={round} kind="ct-up" />}
                                {Array.from({ length: u.matches }, (_, i) => (
                                    <MatchCard key={i} t={t} bracket="B" round={round} index={i + 1} kind="ct-up" />
                                ))}
                            </div>,
                        ];
                    })}

                    <div className="ct-lane ct-lane--u" style={at('6 / 8')}>
                        <span>Финалист верхней</span><span className="ct-cnt">ждёт</span>
                    </div>
                    <div className="ct-cell ct-cell--u" style={at('6 / 8')}>
                        <div className="ct-wait">
                            <p>Победитель верхней сетки пропускает туры 6 и 7 и ждёт соперника в финале.</p>
                            <div className="ct-slot ct-slot--plain">
                                <span className={`ct-name${upperFinalist ? '' : ' is-empty'}`}>
                                    {upperFinalist ? upperFinalist.name : '—'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Переходы сверху вниз */}
                    {Object.entries(DROPS).map(([r, [n, to]]) => (
                        <div key={`d${r}`} className="ct-chip ct-drop" style={at(Number(r))}>
                            <span className="ct-ar">↓</span>
                            <span><b>{n} {plural(n, ['проигравший', 'проигравших', 'проигравших'])}</b> верхней сетки — в нижнюю, тур {to}</span>
                        </div>
                    ))}

                    {/* Нижняя сетка */}
                    {Object.entries(LOWER).map(([r, l]) => {
                        const round = Number(r);
                        const n = OUTS[round];
                        return [
                            <div key={`ll${r}`} className="ct-lane ct-lane--l" style={at(round)}>
                                <span>Нижняя</span><span className="ct-cnt">{l.count}</span>
                            </div>,
                            <div key={`lc${r}`} className="ct-cell ct-cell--l" style={at(round)}>
                                <div className="ct-chip"><span className="ct-ar">←</span><span>{l.source}</span></div>
                                {l.bye && <ByeCard t={t} bracket="H" round={round} kind="ct-lo" />}
                                {Array.from({ length: l.matches }, (_, i) => (
                                    <MatchCard key={i} t={t} bracket="H" round={round} index={i + 1} kind="ct-lo" />
                                ))}
                            </div>,
                            <div key={`lo${r}`} className="ct-chip ct-out" style={at(round)}>
                                <span className="ct-ar">✕</span>
                                <span>{n} {plural(n, ['игрок выбывает', 'игрока выбывают', 'игроков выбывают'])}</span>
                            </div>,
                        ];
                    })}

                    {/* Финал */}
                    <div className="ct-cell ct-cell--fin">
                        {/* Блок финала центрируется на границе верхней и нижней сеток */}
                        <div className="ct-fin-stack">
                            {TROPHY_IMAGE_URL && (
                                <img className="ct-trophy" src={TROPHY_IMAGE_URL} alt="Кубок — главный приз турнира" />
                            )}
                            <div className="ct-final">
                                <div className="ct-final-head">
                                    <h4>Финал</h4>
                                    <span>{formatDate(finalMatch?.date)}</span>
                                </div>
                                <div className="ct-lab ct-lab--u">Победитель верхней сетки</div>
                                <div className="ct-match"><PlayerRow player={playerAt(finalMatch, 1)} /></div>
                                <div className="ct-vs">против</div>
                                <div className="ct-lab ct-lab--l">Победитель нижней сетки</div>
                                <div className="ct-match"><PlayerRow player={playerAt(finalMatch, 2)} /></div>
                                <div className="ct-champ">
                                    <div className="ct-lab">Победитель турнира</div>
                                    <div className={`ct-champ-name${champion ? '' : ' is-empty'}`}>
                                        {champion ? champion.name : 'Определится в финале'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section className="ct-how" ref={howRef}>
                <div><h5>Пары</h5><p>В каждом туре пары составляются заново жребием, поэтому линии между матчами не проводятся.</p></div>
                <div><h5>Переходы</h5><p>Проигравший в верхней сетке опускается в нижнюю на следующий тур. Проигравший в нижней сетке выбывает из турнира.</p></div>
                <div><h5>Нумерация</h5><p>В3·2 — верхняя сетка, тур 3, матч 2. Н4·1 — нижняя сетка, тур 4, матч 1. Справа в заголовке матча — дата игры.</p></div>
            </section>
        </div>
    );
}
